import { supabase } from '../../config/supabase';

export const triggerPushForMessage = async (messageId) => {
    if (!messageId) {
        return { skipped: true, reason: 'missing_message_id' };
    }

    try {
        const { data, error } = await supabase.functions.invoke('send-push-message', {
            body: {
                message_id: messageId,
            }
        });

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error triggering message push notification:', error);
        return {
            success: false,
            error: error.message || 'Unknown message push trigger error'
        };
    }
};

const ensureDirectConversationMembers = async ({
    conversationId,
    companyId,
    currentUserId,
    otherUserId,
}) => {
    const now = new Date().toISOString();

    const rows = [
        {
            conversation_id: conversationId,
            user_id: currentUserId,
            company_id: companyId,
            role: 'member',
            last_read_at: now,
        },
        {
            conversation_id: conversationId,
            user_id: otherUserId,
            company_id: companyId,
            role: 'member',
            last_read_at: null,
        },
    ];

    const { error } = await supabase
        .from('conversation_members')
        .upsert(rows, {
            onConflict: 'conversation_id,user_id',
            ignoreDuplicates: false,
        });

    if (error) throw error;
};


/**
 * Get all conversations for current user
 */
export const getConversations = async (userId, companyId) => {
    try {
        const { data: memberships, error } = await supabase
            .from('conversation_members')
            .select(`
                *,
                conversation:conversations(
                    *,
                    participant1:users!conversations_participant1_id_fkey(id, username, full_name, avatar_url, role),
                    participant2:users!conversations_participant2_id_fkey(id, username, full_name, avatar_url, role)
                )
            `)
            .eq('user_id', userId)
            .eq('company_id', companyId)
            .eq('archived', false)
            .order('joined_at', { ascending: false });

        if (error) throw error;

        const conversationsWithMeta = await Promise.all((memberships || []).map(async (member) => {
            const conv = member.conversation;
            if (!conv) return null;

            const { data: lastMessage } = await supabase
                .from('messages')
                .select('id, content, created_at, sender_id, is_read, attachments')
                .eq('conversation_id', conv.id)
                .is('deleted_at', null)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            const { count } = await supabase
                .from('messages')
                .select('*', { count: 'exact', head: true })
                .eq('conversation_id', conv.id)
                .neq('sender_id', userId)
                .gt('created_at', member.last_read_at || '1970-01-01T00:00:00.000Z')
                .is('deleted_at', null);

            const isGroup = conv.type === 'group';

            const otherUser = !isGroup
                ? conv.participant1_id === userId
                    ? conv.participant2
                    : conv.participant1
                : null;

            return {
                ...conv,
                membership: member,
                isGroup,
                otherUser,
                unreadCount: count || 0,
                lastMessage: lastMessage || null,
                updated_at: lastMessage?.created_at || conv.last_message_at || conv.updated_at,
                last_message_at: lastMessage?.created_at || conv.last_message_at || conv.updated_at,
            };
        }));

        return conversationsWithMeta
            .filter(Boolean)
            .sort((a, b) => {
                const dateA = a.lastMessage?.created_at || a.last_message_at || a.updated_at || a.created_at;
                const dateB = b.lastMessage?.created_at || b.last_message_at || b.updated_at || b.created_at;

                return new Date(dateB || 0) - new Date(dateA || 0);
            });
    } catch (error) {
        console.error('Error fetching conversations:', error);
        throw error;
    }
};

/**
 * Get or create a conversation between two users
 */
export const getOrCreateConversation = async (currentUserId, otherUserId, companyId) => {
    try {
        const { data: existing, error: searchError } = await supabase
            .from('conversations')
            .select(`
                *,
                participant1:users!conversations_participant1_id_fkey(id, username, full_name, avatar_url, role),
                participant2:users!conversations_participant2_id_fkey(id, username, full_name, avatar_url, role)
            `)
            .eq('company_id', companyId)
            .or(`and(participant1_id.eq.${currentUserId},participant2_id.eq.${otherUserId}),and(participant1_id.eq.${otherUserId},participant2_id.eq.${currentUserId})`)
            .maybeSingle();

        if (searchError) throw searchError;

        if (existing) {
            await ensureDirectConversationMembers({
                conversationId: existing.id,
                companyId,
                currentUserId,
                otherUserId,
            });

            await supabase
                .from('conversation_members')
                .update({
                    archived: false,
                    last_read_at: new Date().toISOString(),
                })
                .eq('conversation_id', existing.id)
                .eq('user_id', currentUserId);

            const otherUser =
                existing.participant1_id === currentUserId
                    ? existing.participant2
                    : existing.participant1;

            return { ...existing, otherUser };
        }

        const { data: newConv, error: createError } = await supabase
            .from('conversations')
            .insert({
                company_id: companyId,
                participant1_id: currentUserId,
                participant2_id: otherUserId,
            })
            .select(`
                *,
                participant1:users!conversations_participant1_id_fkey(id, username, full_name, avatar_url, role),
                participant2:users!conversations_participant2_id_fkey(id, username, full_name, avatar_url, role)
            `)
            .single();

        if (createError) throw createError;

        await ensureDirectConversationMembers({
            conversationId: newConv.id,
            companyId,
            currentUserId,
            otherUserId,
        });

        const otherUser =
            newConv.participant1_id === currentUserId
                ? newConv.participant2
                : newConv.participant1;

        return { ...newConv, otherUser };
    } catch (error) {
        console.error('Error getting/creating conversation:', error);
        throw error;
    }
};


export const createGroupConversation = async ({
    companyId,
    creatorId,
    title,
    memberIds = [],
}) => {
    try {
        const cleanMemberIds = [...new Set([creatorId, ...memberIds])];

        if (!title?.trim()) {
            throw new Error('Group name is required');
        }

        if (cleanMemberIds.length < 2) {
            throw new Error('A group needs at least 2 members');
        }

        const { data: conversation, error: convError } = await supabase
            .from('conversations')
            .insert({
                company_id: companyId,
                type: 'group',
                title: title.trim(),
                created_by: creatorId,
                last_message_at: new Date().toISOString(),
            })
            .select('*')
            .single();

        if (convError) throw convError;

        const memberRows = cleanMemberIds.map(userId => ({
            conversation_id: conversation.id,
            user_id: userId,
            company_id: companyId,
            role: userId === creatorId ? 'owner' : 'member',
            last_read_at: userId === creatorId ? new Date().toISOString() : null,
        }));

        const { error: memberError } = await supabase
            .from('conversation_members')
            .insert(memberRows);

        if (memberError) throw memberError;

        return {
            ...conversation,
            isGroup: true,
            membership: memberRows.find(m => m.user_id === creatorId),
            otherUser: null,
            unreadCount: 0,
            lastMessage: null,
        };
    } catch (error) {
        console.error('Error creating group conversation:', error);
        throw error;
    }
};

/**
 * Get messages for a conversation with pagination
 */
export const getMessages = async (conversationId, limit = 30, before = null, userId = null) => {
    try {
        let joinedAt = null;

        if (userId) {
            const { data: membership } = await supabase
                .from('conversation_members')
                .select('joined_at')
                .eq('conversation_id', conversationId)
                .eq('user_id', userId)
                .maybeSingle();

            joinedAt = membership?.joined_at || null;
        }

        let query = supabase
            .from('messages')
            .select(`
                *,
                sender:users!messages_sender_id_fkey(id, username, full_name, avatar_url, role)
            `)
            .eq('conversation_id', conversationId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (joinedAt) {
            query = query.gte('created_at', joinedAt);
        }

        if (before) {
            query = query.lt('created_at', before);
        }

        const { data: messages, error } = await query;

        if (error) throw error;

        return (messages || []).reverse();
    } catch (error) {
        console.error('Error fetching messages:', error);
        throw error;
    }
};

/**
 * Send a message
 */
export const sendMessage = async (
    conversationId,
    senderId,
    receiverId,
    content,
    attachments = null,
    options = {}
) => {
    try {
        const isGroup = options?.isGroup === true;

        const insertPayload = {
            conversation_id: conversationId,
            sender_id: senderId,
            content,
            attachments,
            metadata: options?.metadata || {},
        };

        if (!isGroup && receiverId) {
            insertPayload.receiver_id = receiverId;
        }

        const { data: message, error } = await supabase
            .from('messages')
            .insert(insertPayload)
            .select()
            .single();

        if (error) throw error;



        // If receiver previously deleted/archived this direct conversation,
        // make it visible again when they receive a new message.
        if (!isGroup && receiverId) {
            const { error: memberError } = await supabase
                .from('conversation_members')
                .update({ archived: false })
                .eq('conversation_id', conversationId)
                .eq('user_id', receiverId);

            if (memberError) throw memberError;
        }

        const messageTime = message.created_at || new Date().toISOString();

        await supabase
            .from('conversations')
            .update({
                updated_at: messageTime,
                last_message_at: messageTime,
                last_message_id: message.id,
            })
            .eq('id', conversationId);


        triggerPushForMessage(message.id);

        return message;
    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (conversationId, userId) => {
    try {
        const now = new Date().toISOString();

        // New group-compatible read state
        const { error: memberError } = await supabase
            .from('conversation_members')
            .update({ last_read_at: now })
            .eq('conversation_id', conversationId)
            .eq('user_id', userId);

        if (memberError) throw memberError;

        // Keep old direct-message read state working too
        await supabase
            .from('messages')
            .update({ is_read: true, read_at: now })
            .eq('conversation_id', conversationId)
            .eq('receiver_id', userId)
            .eq('is_read', false);

        return { success: true };
    } catch (error) {
        console.error('Error marking messages as read:', error);
        throw error;
    }
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (conversationId, userId) => {
    try {
        const { error } = await supabase
            .from('conversation_members')
            .update({
                archived: true,
                last_read_at: new Date().toISOString(),
            })
            .eq('conversation_id', conversationId)
            .eq('user_id', userId);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error deleting conversation:', error);
        throw error;
    }
};

/**
 * Get users available for messaging (same company)
 */
export const getAvailableUsers = async (companyId, currentUserId) => {
    try {
        const { data: users, error } = await supabase
            .from('users')
            .select('id, username, full_name, avatar_url, role, status')
            .eq('company_id', companyId)
            .eq('status', 'active')
            .neq('id', currentUserId)
            .order('username');

        if (error) throw error;

        return users;
    } catch (error) {
        console.error('Error fetching available users:', error);
        throw error;
    }
};

/**
 * Get total unread message count
 */
export const getUnreadMessageCount = async (userId, companyId = null) => {
    try {
        if (!userId || userId === 'undefined') return 0;

        let safeCompanyId = companyId;

        // Backwards compatibility:
        // Some old component is still calling getUnreadMessageCount(userId)
        if (!safeCompanyId || safeCompanyId === 'undefined') {
            const { data: userRow, error: userError } = await supabase
                .from('users')
                .select('company_id')
                .eq('id', userId)
                .single();

            if (userError || !userRow?.company_id) {
                console.error('Could not resolve company_id for unread count:', userError);
                return 0;
            }

            safeCompanyId = userRow.company_id;
        }

        const { data: memberships, error: memberError } = await supabase
            .from('conversation_members')
            .select('conversation_id, last_read_at')
            .eq('user_id', userId)
            .eq('company_id', safeCompanyId)
            .eq('archived', false);

        if (memberError) throw memberError;

        if (!memberships?.length) return 0;

        const counts = await Promise.all(
            memberships.map(async (member) => {
                const { count, error } = await supabase
                    .from('messages')
                    .select('*', { count: 'exact', head: true })
                    .eq('conversation_id', member.conversation_id)
                    .neq('sender_id', userId)
                    .gt('created_at', member.last_read_at || '1970-01-01T00:00:00.000Z')
                    .is('deleted_at', null);

                if (error) {
                    console.error('Unread count error:', error);
                    return 0;
                }

                return count || 0;
            })
        );

        return counts.reduce((sum, count) => sum + count, 0);
    } catch (error) {
        console.error('Error fetching unread count:', error);
        return 0;
    }
};

/**
 * Subscribe to new messages
 */
export const subscribeToMessages = (userId, callback) => {
    const channelName = `messages-global-${userId}-${Date.now()}`;

    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
            },
            async (payload) => {
                const message = payload.new;

                if (message.sender_id === userId) return;

                const { data, error } = await supabase
                    .from('messages')
                    .select(`
                        *,
                        sender:users!messages_sender_id_fkey(id, username, full_name, avatar_url, role)
                    `)
                    .eq('id', message.id)
                    .single();

                if (error) {
                    console.error('Global realtime message hydrate error:', error);
                    callback(message);
                    return;
                }

                callback(data);
            }
        )
        .subscribe();

    return subscription;
};

/**
 * Subscribe to message read receipts
 */
export const subscribeToReadReceipts = (userId, callback) => {
    const channelName = `read-receipts-${userId}-${Date.now()}`;

    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'messages',
                filter: `sender_id=eq.${userId}`
            },
            (payload) => {
                if (payload.new.is_read && !payload.old.is_read) {
                    callback(payload.new);
                }
            }
        )
        .subscribe();

    return subscription;
};

/**
 * Subscribe to messages in a specific conversation (for real-time updates)
 */
export const subscribeToConversationMessages = (conversationId, callback) => {
    const channelName = `conversation-${conversationId}-${Date.now()}`;

    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
                filter: `conversation_id=eq.${conversationId}`
            },
            async (payload) => {
                const { data, error } = await supabase
                    .from('messages')
                    .select(`
                        *,
                        sender:users!messages_sender_id_fkey(id, username, full_name, avatar_url, role)
                    `)
                    .eq('id', payload.new.id)
                    .single();

                if (error) {
                    console.error('Realtime message hydrate error:', error);
                    callback(payload.new);
                    return;
                }

                callback(data);
            }
        )
        .subscribe();

    return subscription;
};

/**
 * Upload a file attachment to the private 'message-attachments' storage bucket.
 * Returns the storage path (not a public URL). Use getAttachmentSignedUrl to display.
 */
export const uploadAttachment = async (file, userId) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const path = `${userId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;

    const { error } = await supabase.storage
        .from('message-attachments')
        .upload(path, file, { cacheControl: '3600', upsert: false });

    if (error) throw error;

    return path; // store path, not a public URL
};

/**
 * Generate a signed (time-limited) URL for a private attachment.
 * Default expiry: 1 hour.
 * Pass downloadName to make the URL download the file under that name
 * (the stored path is randomised, so the original name has to be supplied).
 */
export const getAttachmentSignedUrl = async (path, expiresIn = 3600, downloadName = null) => {
    const { data, error } = await supabase.storage
        .from('message-attachments')
        .createSignedUrl(path, expiresIn, downloadName ? { download: downloadName } : undefined);

    if (error) throw error;
    return data.signedUrl;
};

/**
 * Search jobs for embedding as a link in a message
 */
export const searchJobsForLink = async (companyId, query) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .select('id, unique_id, customer_address')
            .eq('company_id', companyId)
            .or(`unique_id.ilike.%${query}%,customer_address.ilike.%${query}%,customer_name.ilike.%${query}%`)
            .order('created_at', { ascending: false })
            .limit(8);

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error searching jobs:', error);
        return [];
    }
};


export const getConversationMembers = async (conversationId) => {
    const { data, error } = await supabase
        .from('conversation_members')
        .select(`
            *,
            user:users(id, username, full_name, avatar_url, role, email)
        `)
        .eq('conversation_id', conversationId)
        .order('role', { ascending: false })
        .order('joined_at', { ascending: true });

    if (error) throw error;
    return data || [];
};

export const addGroupMembers = async ({ conversationId, companyId, userIds }) => {
    const rows = [...new Set(userIds)].map(userId => ({
        conversation_id: conversationId,
        company_id: companyId,
        user_id: userId,
        role: 'member',
        joined_at: new Date().toISOString(),
        last_read_at: new Date().toISOString(),
    }));

    const { error } = await supabase
        .from('conversation_members')
        .insert(rows);

    if (error) throw error;
    return { success: true };
};

export const updateGroupMemberRole = async ({ conversationId, userId, role }) => {
    const { error } = await supabase
        .from('conversation_members')
        .update({ role })
        .eq('conversation_id', conversationId)
        .eq('user_id', userId);

    if (error) throw error;
    return { success: true };
};

export const removeGroupMember = async ({ conversationId, userId }) => {
    const { error } = await supabase
        .from('conversation_members')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', userId);

    if (error) throw error;
    return { success: true };
};

export const transferGroupOwnership = async ({ conversationId, currentOwnerId, newOwnerId }) => {
    const { error: oldOwnerError } = await supabase
        .from('conversation_members')
        .update({ role: 'admin' })
        .eq('conversation_id', conversationId)
        .eq('user_id', currentOwnerId);

    if (oldOwnerError) throw oldOwnerError;

    const { error: newOwnerError } = await supabase
        .from('conversation_members')
        .update({ role: 'owner' })
        .eq('conversation_id', conversationId)
        .eq('user_id', newOwnerId);

    if (newOwnerError) throw newOwnerError;

    const { error: convError } = await supabase
        .from('conversations')
        .update({ created_by: newOwnerId })
        .eq('id', conversationId);

    if (convError) throw convError;

    return { success: true };
};

export const sendSystemMessage = async ({
    conversationId,
    senderId,
    content,
    action,
    metadata = {},
}) => {
    const { data: message, error } = await supabase
        .from('messages')
        .insert({
            conversation_id: conversationId,
            sender_id: senderId,
            content,
            message_type: 'system',
            system_action: action,
            metadata,
            attachments: null,
        })
        .select()
        .single();

    if (error) throw error;

    const messageTime = message.created_at || new Date().toISOString();

    await supabase
        .from('conversations')
        .update({
            updated_at: messageTime,
            last_message_at: messageTime,
            last_message_id: message.id,
        })
        .eq('id', conversationId);

    return message;
};